import type { CompanyMembership, Customer } from '@horevia/contracts';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dialog,
  DialogClose,
  DialogContent,
  EmptyState,
  ErrorState,
  IconButton,
  NativeSelect,
  NoPermissionState,
  Notice,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tab,
  TabPanel,
  Tabs,
  TabsList,
  Table,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  toast,
} from '@horevia/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  BedDouble,
  Building2,
  FileBadge,
  HeartHandshake,
  Merge,
  MessageSquare,
  Pencil,
  Plus,
  Receipt,
  StickyNote,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can, useCan, useHasModule } from '@/features/auth/session';
import { ConsentsCard } from '@/features/crm/ConsentsCard';
import { InteractionsPanel } from '@/features/crm/InteractionsPanel';
import { LoyaltyPanel } from '@/features/crm/LoyaltyPanel';
import { MergeDialog } from '@/features/crm/MergeDialog';
import { CustomerDocumentsTab } from '@/features/documents/CustomerDocumentsTab';
import { CustomerInvoicesCard } from '@/features/billing/CustomerInvoicesCard';
import { CustomerPaymentsCard } from '@/features/payments/CustomerPaymentsCard';
import { CustomerFormDrawer } from '@/features/customers/CustomerFormDrawer';
import { CustomerSearchField } from '@/features/customers/CustomerSearchField';
import {
  archiveCustomer,
  createMembership,
  customerKeys,
  customerQueryOptions,
  customerReservationsQueryOptions,
  deleteMembership,
  restoreCustomer,
} from '@/features/customers/queries';
import { reservationsQueryOptions } from '@/features/reservations/queries';
import { reservationTone } from '@/features/reservations/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDate, formatPhone } from '@/lib/format';

export const Route = createFileRoute('/_app/customers/$customerId')({ component: CustomerPage });

function CustomerPage() {
  const { customerId } = Route.useParams();
  const { t, i18n } = useTranslation(['customers', 'reservations', 'common', 'errors', 'crm']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canRead = useCan('customer.read');
  const canUpdate = useCan('customer.update');
  // Horevia CRM (ADR-025) : onglets « Fidélité » et « Interactions » avec le module et `crm.read`.
  const hasCrmModule = useHasModule('crm');
  const canReadCrm = useCan('crm.read');
  const hasCrm = hasCrmModule && canReadCrm;
  const canMerge = useCan('customer.merge');
  const [mergeOpen, setMergeOpen] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  const query = useQuery({ ...customerQueryOptions(customerId), enabled: canRead });
  // Séjours du client : route dédiée `GET /customers/:id/reservations`
  // (filtre serveur sur l'occupant principal).
  const staysQuery = useInfiniteQuery(customerReservationsQueryOptions(customerId, canRead, 10));
  const billedQuery = useInfiniteQuery({
    ...reservationsQueryOptions({ billingPartyId: customerId }, 10),
    enabled: canRead,
  });

  const archiveMutation = useMutation({
    mutationFn: () =>
      query.data?.isArchived ? restoreCustomer(customerId) : archiveCustomer(customerId),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: customerKeys.all });
      toast.success(
        updated.isArchived ? t('customers:toasts.archived') : t('customers:toasts.restored'),
      );
      setArchiveOpen(false);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const unlinkMutation = useMutation({
    mutationFn: (membershipId: string) => deleteMembership(membershipId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: customerKeys.all });
      toast.success(t('customers:toasts.unlinked'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  if (query.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={120} />
          <Skeleton height={320} />
        </div>
      </Container>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const customer = query.data;
  const isPerson = customer.kind === 'PERSON';
  const stays = staysQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const billed = billedQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const memberships = customer.memberships.filter((link) =>
    isPerson ? link.personId === customer.id : link.companyId === customer.id,
  );

  return (
    <Container>
      <PageHeader
        title={customer.displayName}
        subtitle={
          isPerson
            ? [customer.person?.profession, formatPhone(customer.phonePrimary), customer.email]
                .filter(Boolean)
                .join(' · ')
            : [customer.company?.ifu ? `IFU ${customer.company.ifu}` : null, customer.address?.city]
                .filter(Boolean)
                .join(' · ')
        }
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: isPerson ? t('customers:people.title') : t('customers:companies.title'),
            link: (
              <Link to={isPerson ? '/customers/people' : '/customers/companies'}>
                {isPerson ? t('customers:people.title') : t('customers:companies.title')}
              </Link>
            ),
          },
          { label: customer.displayName },
        ]}
        actions={
          <>
            <Can permission="reservation.create">
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate({
                    to: '/reservations/new',
                    search: isPerson ? { guestId: customer.id } : { billingPartyId: customer.id },
                  })
                }
              >
                <Plus className="ico" aria-hidden="true" />
                {t('customers:actions.newReservation')}
              </Button>
            </Can>
            {canMerge && !customer.mergedIntoId && !customer.isArchived ? (
              <Button variant="secondary" onClick={() => setMergeOpen(true)}>
                <Merge className="ico" aria-hidden="true" />
                {t('crm:merge.action')}
              </Button>
            ) : null}
            {hasCrm ? (
              <Button variant="secondary" asChild>
                <Link to="/crm/customers/$customerId" params={{ customerId }}>
                  <HeartHandshake className="ico" aria-hidden="true" />
                  {t('crm:actions.openCrm')}
                </Link>
              </Button>
            ) : null}
            <Can permission="customer.update">
              <Button variant="primary" onClick={() => setEditOpen(true)}>
                <Pencil className="ico" aria-hidden="true" />
                {t('common:actions.edit')}
              </Button>
            </Can>
          </>
        }
      />

      <div className="grid grid--main-aside">
        <div>
          <Tabs defaultValue="stays">
            <TabsList pills aria-label={t('customers:detail.tabsLabel')}>
              <Tab value="stays" icon={<BedDouble className="ico" aria-hidden="true" />}>
                {t('customers:detail.tabs.stays')}
              </Tab>
              <Tab value="billing" icon={<Receipt className="ico" aria-hidden="true" />}>
                {t('customers:detail.tabs.billing')}
              </Tab>
              <Tab
                value="links"
                icon={
                  isPerson ? (
                    <Building2 className="ico" aria-hidden="true" />
                  ) : (
                    <Users className="ico" aria-hidden="true" />
                  )
                }
                count={memberships.length}
              >
                {isPerson
                  ? t('customers:detail.tabs.companies')
                  : t('customers:detail.tabs.employees')}
              </Tab>
              <Tab value="documents" icon={<FileBadge className="ico" aria-hidden="true" />}>
                {t('customers:detail.tabs.documents')}
              </Tab>
              <Tab value="notes" icon={<StickyNote className="ico" aria-hidden="true" />}>
                {t('customers:detail.tabs.notes')}
              </Tab>
              {hasCrm ? (
                <Tab value="loyalty" icon={<HeartHandshake className="ico" aria-hidden="true" />}>
                  {t('customers:detail.tabs.loyalty')}
                </Tab>
              ) : null}
              {hasCrm ? (
                <Tab
                  value="interactions"
                  icon={<MessageSquare className="ico" aria-hidden="true" />}
                >
                  {t('customers:detail.tabs.interactions')}
                </Tab>
              ) : null}
            </TabsList>

            <TabPanel value="stays">
              <Card>
                <CardHeader
                  title={t('customers:detail.staysTitle')}
                  icon={<BedDouble className="ico ico-16" aria-hidden="true" />}
                />
                <CardBody density="flush">
                  {stays.length === 0 ? (
                    <EmptyState
                      icon={<BedDouble className="ico ico-24" aria-hidden="true" />}
                      title={t('customers:detail.staysEmptyTitle')}
                      description={t('customers:detail.staysEmptyBody')}
                    />
                  ) : (
                    <ReservationTable rows={stays} />
                  )}
                </CardBody>
              </Card>
            </TabPanel>

            <TabPanel value="billing">
              <div className="stack">
                <div className="money-strip">
                  <div className="money-strip__cell">
                    <div className="money-strip__l">{t('customers:detail.staysCount')}</div>
                    <div className="money-strip__v">{customer.stats.reservationsCount}</div>
                  </div>
                  <div className="money-strip__cell money-strip__cell--due">
                    <div className="money-strip__l">{t('customers:detail.balance')}</div>
                    <div className="money-strip__v">
                      {formatAmount(customer.stats.balance, i18n.language)}
                    </div>
                  </div>
                  <div className="money-strip__cell">
                    <div className="money-strip__l">{t('customers:detail.lastStay')}</div>
                    <div className="money-strip__v t-18">
                      {customer.stats.lastStayAt
                        ? formatDate(customer.stats.lastStayAt, i18n.language)
                        : t('customers:never')}
                    </div>
                  </div>
                </div>

                <Card>
                  <CardHeader
                    title={t('customers:detail.billedTitle')}
                    icon={<Receipt className="ico ico-16" aria-hidden="true" />}
                    iconTone="amber"
                  />
                  <CardBody density="flush">
                    {billed.length === 0 ? (
                      <EmptyState
                        icon={<Receipt className="ico ico-24" aria-hidden="true" />}
                        title={t('customers:detail.billedEmptyTitle')}
                        description={t('customers:detail.billedEmptyBody')}
                      />
                    ) : (
                      <ReservationTable rows={billed} />
                    )}
                  </CardBody>
                </Card>

                {/* Factures du client (sprint 2.5). */}
                <CustomerInvoicesCard customerId={customer.id} />

                {/* Encaissements du client (sprint 2.6). */}
                <CustomerPaymentsCard customerId={customer.id} />
              </div>
            </TabPanel>

            <TabPanel value="links">
              <Card>
                <CardHeader
                  title={
                    isPerson
                      ? t('customers:detail.tabs.companies')
                      : t('customers:detail.tabs.employees')
                  }
                  icon={
                    isPerson ? (
                      <Building2 className="ico ico-16" aria-hidden="true" />
                    ) : (
                      <Users className="ico ico-16" aria-hidden="true" />
                    )
                  }
                  actions={
                    canUpdate ? (
                      <Button variant="secondary" size="sm" onClick={() => setLinkOpen(true)}>
                        <Plus className="ico ico-14" aria-hidden="true" />
                        {isPerson
                          ? t('customers:detail.linkCompany')
                          : t('customers:detail.linkPerson')}
                      </Button>
                    ) : null
                  }
                />
                <CardBody density="flush">
                  {memberships.length === 0 ? (
                    <EmptyState
                      icon={<Building2 className="ico ico-24" aria-hidden="true" />}
                      title={t('customers:detail.linksEmptyTitle')}
                      description={t('customers:detail.linksEmptyBody')}
                    />
                  ) : (
                    <MembershipTable
                      memberships={memberships}
                      isPerson={isPerson}
                      canUpdate={canUpdate}
                      onUnlink={(id) => unlinkMutation.mutate(id)}
                    />
                  )}
                </CardBody>
              </Card>
            </TabPanel>

            <TabPanel value="documents">
              <CustomerDocumentsTab customerId={customer.id} isCompany={!isPerson} />
            </TabPanel>

            <TabPanel value="notes">
              <Card>
                <CardHeader
                  title={t('customers:detail.tabs.notes')}
                  icon={<StickyNote className="ico ico-16" aria-hidden="true" />}
                  iconTone="blue"
                />
                <CardBody>
                  {customer.notes ? (
                    <p className="t-13 mb-0">{customer.notes}</p>
                  ) : (
                    <p className="t-13 muted mb-0">{t('customers:detail.notesEmpty')}</p>
                  )}
                </CardBody>
              </Card>
            </TabPanel>
            {hasCrm ? (
              <TabPanel value="loyalty">
                <LoyaltyPanel customerId={customer.id} />
              </TabPanel>
            ) : null}
            {hasCrm ? (
              <TabPanel value="interactions">
                <InteractionsPanel
                  customerId={customer.id}
                  email={customer.email}
                  emailConsent={customer.consents.some(
                    (state) => state.channel === 'EMAIL' && state.granted,
                  )}
                />
              </TabPanel>
            ) : null}
          </Tabs>
        </div>

        <aside className="stack sticky-aside">
          <Card variant="raised">
            <CardBody density="tight">
              <div className="row row--top" style={{ gap: 12 }}>
                <Avatar name={customer.displayName} size="xl" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="t-16 strong">{customer.displayName}</div>
                  <div className="row row--wrap mt-2" style={{ gap: 6 }}>
                    <Badge tone={isPerson ? 'violet' : 'blue'}>
                      {t(`customers:kind.${customer.kind}`)}
                    </Badge>
                    {customer.isArchived ? (
                      <Badge tone="neutral">{t('customers:archived')}</Badge>
                    ) : null}
                    {customer.tags.map((tag) => (
                      <Badge key={tag} tone="green">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              <div className="kv-grid mt-4">
                <div>
                  <div className="kv__k">{t('customers:fields.phonePrimary')}</div>
                  <div className="kv__v t-13">{formatPhone(customer.phonePrimary) || '—'}</div>
                </div>
                <div>
                  <div className="kv__k">{t('customers:fields.email')}</div>
                  <div className="kv__v t-13">{customer.email ?? '—'}</div>
                </div>
                <div>
                  <div className="kv__k">{t('customers:fields.address')}</div>
                  <div className="kv__v t-13">
                    {[customer.address?.line1, customer.address?.poBox, customer.address?.city]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </div>
                </div>
                {isPerson ? (
                  <>
                    <div>
                      <div className="kv__k">{t('customers:fields.idDocument')}</div>
                      <div className="kv__v t-13">
                        {customer.person?.idDocumentNumber
                          ? `${customer.person.idDocumentType ?? ''} ${customer.person.idDocumentNumber}`
                          : t('customers:detail.idMissing')}
                      </div>
                    </div>
                    <div>
                      <div className="kv__k">{t('customers:fields.birthDate')}</div>
                      <div className="kv__v t-13">
                        {customer.person?.birthDate
                          ? formatDate(`${customer.person.birthDate}T00:00:00Z`, i18n.language)
                          : '—'}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div className="kv__k">RCCM</div>
                      <div className="kv__v t-13">{customer.company?.rccm ?? '—'}</div>
                    </div>
                    <div>
                      <div className="kv__k">IFU</div>
                      <div className="kv__v t-13">{customer.company?.ifu ?? '—'}</div>
                    </div>
                    <div>
                      <div className="kv__k">{t('customers:fields.taxRegime')}</div>
                      <div className="kv__v t-13">{customer.company?.taxRegime ?? '—'}</div>
                    </div>
                    <div>
                      <div className="kv__k">{t('customers:fields.withholdingDefault')}</div>
                      <div className="kv__v t-13">
                        {customer.company?.withholdingDefault
                          ? t('common:actions.yes')
                          : t('common:actions.no')}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardBody>
          </Card>

          {customer.mergedIntoId ? (
            <Notice tone="info" title={t('crm:merge.mergedInto')}>
              <Link
                className="link-quiet t-13"
                to="/customers/$customerId"
                params={{ customerId: customer.mergedIntoId }}
              >
                {t('crm:merge.openTarget')}
              </Link>
            </Notice>
          ) : null}

          <ConsentsCard
            customerId={customer.id}
            consents={customer.consents}
            readOnly={Boolean(customer.mergedIntoId) || customer.isArchived}
          />

          {isPerson && customer.person && !customer.person.idDocumentNumber ? (
            <Notice tone="warning" title={t('customers:detail.complianceTitle')}>
              <p className="mb-0 t-12">{t('customers:detail.complianceBody')}</p>
            </Notice>
          ) : null}

          <Can permission="customer.update">
            <Card>
              <CardBody density="tight">
                <Button variant="ghost" block onClick={() => setArchiveOpen(true)}>
                  {customer.isArchived ? (
                    <ArchiveRestore className="ico ico-16" aria-hidden="true" />
                  ) : (
                    <Archive className="ico ico-16" aria-hidden="true" />
                  )}
                  {customer.isArchived
                    ? t('customers:actions.restore')
                    : t('customers:actions.archive')}
                </Button>
              </CardBody>
            </Card>
          </Can>
        </aside>
      </div>

      <CustomerFormDrawer open={editOpen} onOpenChange={setEditOpen} customer={customer} />
      <MergeDialog target={customer} open={mergeOpen} onOpenChange={setMergeOpen} />

      <LinkMembershipDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        customer={customer}
        isPerson={isPerson}
      />

      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent
          title={
            customer.isArchived
              ? t('customers:actions.restoreTitle', { name: customer.displayName })
              : t('customers:actions.archiveTitle', { name: customer.displayName })
          }
          description={
            customer.isArchived
              ? t('customers:actions.restoreBody')
              : t('customers:actions.archiveBody')
          }
          icon={<Archive className="ico ico-16" aria-hidden="true" />}
          iconTone="amber"
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <DialogClose asChild>
                <Button variant="ghost">{t('common:actions.cancel')}</Button>
              </DialogClose>
              <Button
                variant={customer.isArchived ? 'primary' : 'danger'}
                loading={archiveMutation.isPending}
                onClick={() => archiveMutation.mutate()}
              >
                {customer.isArchived
                  ? t('customers:actions.restore')
                  : t('customers:actions.archive')}
              </Button>
            </>
          }
        />
      </Dialog>
    </Container>
  );
}

/** Table compacte de séjours réutilisée par les onglets Séjours et Facturation. */
function ReservationTable({
  rows,
}: {
  rows: {
    id: string;
    reference: string;
    status: Parameters<typeof reservationTone>[0];
    resource: { name: string };
    arrivalAt: string;
    departureAt: string;
    totals: { net: number; balance: number };
  }[];
}) {
  const { t, i18n } = useTranslation(['reservations', 'customers', 'common']);
  return (
    <TableWrap>
      <Table compact>
        <thead>
          <tr>
            <Th>{t('reservations:columns.reference')}</Th>
            <Th>{t('reservations:columns.resource')}</Th>
            <Th>{t('reservations:columns.dates')}</Th>
            <Th align="right">{t('reservations:columns.total')}</Th>
            <Th>{t('reservations:columns.status')}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Tr key={row.id}>
              <Td>
                <Link to="/reservations/$reservationId" params={{ reservationId: row.id }}>
                  <span className="cell-id">{row.reference}</span>
                </Link>
              </Td>
              <Td>{row.resource.name}</Td>
              <Td>
                <div className="t-13" style={{ whiteSpace: 'nowrap' }}>
                  {formatDate(row.arrivalAt, i18n.language)}
                </div>
                <div className="t-12 muted" style={{ whiteSpace: 'nowrap' }}>
                  → {formatDate(row.departureAt, i18n.language)}
                </div>
              </Td>
              <Td align="right" numeric>
                {formatAmount(row.totals.net, i18n.language)}
              </Td>
              <Td>
                <StatusBadge status={reservationTone(row.status)}>
                  {t(`reservations:status.${row.status}`)}
                </StatusBadge>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}

function MembershipTable({
  memberships,
  isPerson,
  canUpdate,
  onUnlink,
}: {
  memberships: CompanyMembership[];
  isPerson: boolean;
  canUpdate: boolean;
  onUnlink: (id: string) => void;
}) {
  const { t } = useTranslation(['customers', 'common']);
  return (
    <TableWrap>
      <Table compact>
        <thead>
          <tr>
            <Th>{isPerson ? t('customers:columns.company') : t('customers:columns.person')}</Th>
            <Th>{t('customers:columns.role')}</Th>
            <Th>{t('customers:columns.primary')}</Th>
            {canUpdate ? <Th align="right">{t('common:actions.actions')}</Th> : null}
          </tr>
        </thead>
        <tbody>
          {memberships.map((link) => (
            <Tr key={link.id}>
              <Td>
                <Link
                  to="/customers/$customerId"
                  params={{ customerId: isPerson ? link.companyId : link.personId }}
                >
                  {isPerson ? link.companyName : link.personName}
                </Link>
              </Td>
              <Td>{t(`customers:membershipRole.${link.role}`)}</Td>
              <Td>
                {link.isPrimary ? (
                  <Badge tone="violet">{t('customers:detail.primaryBadge')}</Badge>
                ) : (
                  <span className="muted">—</span>
                )}
              </Td>
              {canUpdate ? (
                <Td align="right">
                  <IconButton
                    label={t('customers:detail.unlink')}
                    onClick={() => onUnlink(link.id)}
                  >
                    <Trash2 className="ico ico-16" aria-hidden="true" />
                  </IconButton>
                </Td>
              ) : null}
            </Tr>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}

function LinkMembershipDialog({
  open,
  onOpenChange,
  customer,
  isPerson,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer;
  isPerson: boolean;
}) {
  const { t } = useTranslation(['customers', 'common', 'errors']);
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<{ id: string; displayName: string } | null>(null);
  const [role, setRole] = useState('EMPLOYEE');

  const mutation = useMutation({
    mutationFn: () =>
      createMembership({
        personId: isPerson ? customer.id : (target?.id ?? ''),
        companyId: isPerson ? (target?.id ?? '') : customer.id,
        role: role as 'EMPLOYEE',
        isPrimary: false,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: customerKeys.all });
      toast.success(t('customers:toasts.linked'));
      setTarget(null);
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={isPerson ? t('customers:detail.linkCompany') : t('customers:detail.linkPerson')}
        description={t('customers:detail.linkBody')}
        icon={
          isPerson ? (
            <Building2 className="ico ico-16" aria-hidden="true" />
          ) : (
            <User className="ico ico-16" aria-hidden="true" />
          )
        }
        closeLabel={t('common:actions.close')}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">{t('common:actions.cancel')}</Button>
            </DialogClose>
            <Button
              variant="primary"
              disabled={!target}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {t('customers:detail.linkSubmit')}
            </Button>
          </>
        }
      >
        <div className="stack">
          <CustomerSearchField
            label={isPerson ? t('customers:fields.company') : t('customers:fields.person')}
            value={
              target
                ? {
                    id: target.id,
                    kind: isPerson ? 'COMPANY' : 'PERSON',
                    displayName: target.displayName,
                    email: null,
                    phonePrimary: null,
                    primaryCompany: null,
                  }
                : null
            }
            onChange={(value) =>
              setTarget(value ? { id: value.id, displayName: value.displayName } : null)
            }
            kind={isPerson ? 'COMPANY' : 'PERSON'}
            placeholder={
              isPerson
                ? t('customers:search.companyPlaceholder')
                : t('customers:search.personPlaceholder')
            }
            required
          />
          <Toolbar>
            <label className="label" htmlFor="membership-role">
              {t('customers:columns.role')}
            </label>
            <NativeSelect
              id="membership-role"
              value={role}
              onChange={(event) => setRole(event.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="EMPLOYEE">{t('customers:membershipRole.EMPLOYEE')}</option>
              <option value="MANAGER">{t('customers:membershipRole.MANAGER')}</option>
              <option value="BILLING_CONTACT">
                {t('customers:membershipRole.BILLING_CONTACT')}
              </option>
              <option value="OTHER">{t('customers:membershipRole.OTHER')}</option>
            </NativeSelect>
          </Toolbar>
        </div>
      </DialogContent>
    </Dialog>
  );
}
